import { motion, useInView, useMotionValue, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Award, Users, Building2, CheckCircle, Code, Monitor, Cpu, Terminal, Database, Wifi } from "lucide-react";
import { LogoIcon } from "../LogoWithText";
import { RevealOnScroll } from "../effects/AnimatedElements";

const defaultContent = {
  section_label: "Nuestro Equipo",
  title: "Liderado por el Ing. Eriberto Fernandez Aquino",
  role: "Ingeniero en Sistemas Operativos / Director General",
  description: "Con más de dos décadas de experiencia en el mercado tecnológico, nuestro equipo de ingenieros expertos desarrolla soluciones que transforman la manera en que las empresas operan.",
  team_image: "https://images.unsplash.com/photo-1723987135977-ae935608939e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHx0ZWNoJTIwdGVhbSUyMHdvcmtpbmclMjBjb21wdXRlcnMlMjBjb2RpbmclMjBzY3JlZW5zfGVufDB8fHx8MTc3MzI3MjY4MHww&ixlib=rb-4.1.0&q=85",
  highlights: [
    "Especialistas en desarrollo de sistemas integrales",
    "Soluciones personalizadas para cada industria",
    "Compromiso con la innovación tecnológica",
    "Socio estratégico de su empresa"
  ]
};

const stats = [
  { icon: Award, value: 20, suffix: "+", label: "Años de Experiencia", color: "#3b82f6" },
  { icon: Users, value: 500, suffix: "+", label: "Clientes Satisfechos", color: "#06b6d4" },
  { icon: Building2, value: 50, suffix: "+", label: "Sectores Atendidos", color: "#8b5cf6" },
];

const AnimatedCounter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const floatingIcons = [
  { Icon: Terminal, x: "-10%", y: "20%", delay: 0, size: 18 },
  { Icon: Database, x: "105%", y: "30%", delay: 1.5, size: 16 },
  { Icon: Wifi, x: "95%", y: "70%", delay: 3, size: 14 },
  { Icon: Code, x: "-5%", y: "75%", delay: 0.8, size: 16 },
];

const DirectorSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [5, -5]);
  const rotateY = useTransform(mouseX, [-300, 300], [-5, 5]);

  const handleMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <section
      id="director"
      ref={sectionRef}
      data-testid="director-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#0b1221]" />
      
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-0 w-[500px] h-[500px] morph-blob"
        style={{ background: "radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 70%)" }}
        animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] morph-blob"
        style={{ background: "radial-gradient(circle, rgba(6, 182, 212, 0.06) 0%, transparent 70%)" }}
        animate={{ x: [0, -40, 0], y: [0, -30, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating tech icons */}
      {floatingIcons.map(({ Icon, x, y, delay, size }, i) => (
        <motion.div
          key={i}
          className="absolute hidden lg:flex items-center justify-center w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm border border-white/10"
          style={{ left: x, top: y }}
          initial={{ opacity: 0, scale: 0 }}
          animate={isInView ? {
            opacity: [0, 0.6, 0.4],
            scale: 1,
            y: [0, -15, 0, 10, 0],
          } : {}}
          transition={{ delay: delay + 0.5, duration: 6, repeat: Infinity, repeatType: "reverse" }}
        >
          <Icon size={size} className="text-[#3b82f6]/60" />
        </motion.div>
      ))}

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image side with 3D tilt */}
          <RevealOnScroll direction="left">
            <div
              className="relative max-w-lg mx-auto"
              onMouseMove={handleMouse}
              onMouseLeave={handleMouseLeave}
              style={{ perspective: 1000 }}
            >
              <motion.div style={{ rotateX, rotateY }} className="relative">
                {/* Animated border frame */}
                <motion.div
                  className="absolute -top-4 -left-4 w-full h-full border-2 border-[#3b82f6]/30"
                  animate={isInView ? {
                    borderColor: ["rgba(59,130,246,0.3)", "rgba(6,182,212,0.5)", "rgba(59,130,246,0.3)"]
                  } : {}}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                
                {/* Scan line effect */}
                <div className="absolute inset-0 overflow-hidden z-30 pointer-events-none">
                  <div className="scan-line" />
                </div>

                <div className="relative bg-[#02040a] border border-[#3b82f6]/20 overflow-hidden">
                  <motion.div
                    className="aspect-[4/3] bg-cover bg-center"
                    style={{ backgroundImage: `url('${data.team_image}')` }}
                    initial={{ scale: 1.1 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-[#02040a]/60 to-transparent" />
                  
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 p-6"
                    initial={{ y: 30, opacity: 0 }}
                    animate={isInView ? { y: 0, opacity: 1 } : {}}
                    transition={{ delay: 0.6, duration: 0.6 }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <LogoIcon size={56} />
                      <div>
                        <h3 className="text-white font-bold text-xl tracking-wide">GLOBAL <span className="text-[#3b82f6]">METAFORA</span></h3>
                        <p className="text-[#06b6d4] font-semibold tracking-wider">APP</p>
                      </div>
                    </div>
                    <p className="text-[#94a3b8] text-sm">
                      Equipo de Ingenieros Especializados en Sistemas Operativos
                    </p>
                  </motion.div>

                  <div className="absolute top-4 right-4 flex gap-2">
                    {[
                      { Ic: Code, color: "#3b82f6", bg: "#3b82f6" },
                      { Ic: Monitor, color: "#06b6d4", bg: "#06b6d4" },
                      { Ic: Cpu, color: "#8b5cf6", bg: "#8b5cf6" },
                    ].map(({ Ic, color, bg }, i) => (
                      <motion.div
                        key={i}
                        className="w-10 h-10 flex items-center justify-center"
                        style={{ backgroundColor: `${bg}20` }}
                        initial={{ opacity: 0, y: -20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.8 + i * 0.15 }}
                        whileHover={{ scale: 1.2, backgroundColor: `${bg}40` }}
                      >
                        <Ic className="w-5 h-5" style={{ color }} />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Animated badge */}
              <motion.div
                className="absolute -bottom-6 -right-6 glass-card p-4 border border-[#3b82f6]/30"
                initial={{ opacity: 0, scale: 0.5, x: 20 }}
                animate={isInView ? { opacity: 1, scale: 1, x: 0 } : {}}
                transition={{ delay: 1, type: "spring", bounce: 0.4 }}
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(59,130,246,0.3)" }}
              >
                <div className="flex items-center gap-4">
                  <motion.div
                    className="w-12 h-12 bg-[#3b82f6] flex items-center justify-center transform -skew-x-6"
                    animate={{ boxShadow: ["0 0 10px rgba(59,130,246,0.3)", "0 0 25px rgba(59,130,246,0.6)", "0 0 10px rgba(59,130,246,0.3)"] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Award className="w-6 h-6 text-white skew-x-6" />
                  </motion.div>
                  <div>
                    <p className="text-[#3b82f6] font-bold text-2xl">
                      <AnimatedCounter value={20} suffix="+" />
                    </p>
                    <p className="text-[#94a3b8] text-sm">Años de Experiencia</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </RevealOnScroll>

          {/* Content side */}
          <RevealOnScroll direction="right">
            <motion.span
              className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 block"
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 }}
            >
              {data.section_label}
            </motion.span>
            
            <motion.h2
              data-testid="director-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {data.title}
            </motion.h2>
            
            <motion.p
              className="text-[#06b6d4] font-medium mb-6"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 }}
            >
              {data.role}
            </motion.p>

            <motion.p
              className="text-[#94a3b8] text-lg mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
            >
              {data.description}
            </motion.p>

            {/* Animated highlights */}
            <div className="space-y-3 mb-10">
              {data.highlights.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 group"
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + index * 0.12, type: "spring" }}
                >
                  <motion.div
                    whileHover={{ scale: 1.3, rotate: 360 }}
                    transition={{ duration: 0.4 }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0" />
                  </motion.div>
                  <span className="text-[#94a3b8] group-hover:text-white transition-colors duration-300">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* Animated Stats */}
            <div className="grid grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center glass-card p-4 relative overflow-hidden group"
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 1 + index * 0.15 }}
                  whileHover={{ scale: 1.05, borderColor: stat.color }}
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `radial-gradient(circle at center, ${stat.color}10, transparent)` }}
                  />
                  <stat.icon className="w-6 h-6 mx-auto mb-2" style={{ color: stat.color }} />
                  <p className="text-2xl font-bold text-white">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-[#94a3b8] text-xs">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
};

export default DirectorSection;
